import React, { useState, useEffect } from 'react';
import { Drawer, Checkbox, Input, Space, Button, message, Spin, Empty } from 'antd';
import { SearchOutlined, CheckOutlined } from '@ant-design/icons';
import rbacService from '../../services/rbacService';

/**
 * Assign Permissions Drawer Component
 * Allows assigning permissions to a role with search and select all functionality
 */
export default function AssignPermissionsDrawer({ visible, role, onClose, onSuccess }) {
  const [permissions, setPermissions] = useState([]);
  const [assignedPermissionIds, setAssignedPermissionIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    if (visible && role) {
      fetchPermissions();
      fetchRolePermissions();
    }
  }, [visible, role]);

  const fetchPermissions = async () => {
    setLoading(true);
    try {
      const result = await rbacService.getPermissions();
      if (result.success) {
        // Ensure data is always an array
        const data = result.data;
        setPermissions(Array.isArray(data) ? data : []);
      } else {
        message.error('Failed to load permissions');
        setPermissions([]);
      }
    } catch (error) {
      message.error('Error loading permissions');
      console.error('Error fetching permissions:', error);
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchRolePermissions = async () => {
    try {
      const result = await rbacService.getRolePermissions(role.id);
      if (result.success) {
        // Ensure data is always an array
        const data = result.data;
        const assignedIds = (Array.isArray(data) ? data : []).map((p) => p.id);
        setAssignedPermissionIds(assignedIds);
      } else {
        setAssignedPermissionIds([]);
      }
    } catch (error) {
      console.error('Error fetching role permissions:', error);
      setAssignedPermissionIds([]);
    }
  };

  const handleTogglePermission = (permissionId) => {
    setAssignedPermissionIds((prev) => {
      if (prev.includes(permissionId)) {
        return prev.filter((id) => id !== permissionId);
      } else {
        return [...prev, permissionId];
      }
    });
  };

  const handleSelectAll = () => {
    const filtered = getFilteredPermissions();
    const allIds = filtered.map((p) => p.id);
    const allSelected = allIds.every((id) => assignedPermissionIds.includes(id));

    if (allSelected) {
      // Deselect all filtered
      setAssignedPermissionIds((prev) =>
        prev.filter((id) => !allIds.includes(id))
      );
    } else {
      // Select all filtered
      setAssignedPermissionIds((prev) => {
        const newIds = [...prev];
        allIds.forEach((id) => {
          if (!newIds.includes(id)) {
            newIds.push(id);
          }
        });
        return newIds;
      });
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await rbacService.assignPermissionsToRole(role.id, {
        permission_ids: assignedPermissionIds,
      });
      if (result.success) {
        message.success('Permissions assigned successfully');
        onSuccess?.();
        onClose();
      } else {
        message.error(result.message || 'Failed to assign permissions');
      }
    } catch (error) {
      message.error('Error assigning permissions');
      console.error('Error assigning permissions:', error);
    } finally {
      setSaving(false);
    }
  };

  const getFilteredPermissions = () => {
    if (!searchText.trim()) {
      return permissions;
    }
    const lowerSearch = searchText.toLowerCase();
    return permissions.filter(
      (p) =>
        p.key?.toLowerCase().includes(lowerSearch) ||
        p.description?.toLowerCase().includes(lowerSearch)
    );
  };

  const filteredPermissions = getFilteredPermissions();
  const allFilteredSelected =
    filteredPermissions.length > 0 &&
    filteredPermissions.every((p) => assignedPermissionIds.includes(p.id));

  return (
    <Drawer
      title={`Assign Permissions - ${role?.name}`}
      open={visible}
      onClose={onClose}
      width={600}
      destroyOnClose
      styles={{
        body: {
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
          paddingBottom: 24,
        },
      }}
      footer={
        <Space className="float-right">
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" onClick={handleSave} loading={saving}>
            Save Permissions
          </Button>
        </Space>
      }
    >
      <div className="flex flex-col flex-1 min-h-0">
        <div className="shrink-0 mb-4">
          <Input
            placeholder="Search permissions..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
          />
        </div>

        <div className="shrink-0 mb-4 flex justify-between items-center">
          <span className="text-sm text-gray-600">
            {assignedPermissionIds.length} of {permissions.length} permissions selected
          </span>
          {filteredPermissions.length > 0 && (
            <Button
              type="link"
              size="small"
              onClick={handleSelectAll}
            >
              {allFilteredSelected ? 'Deselect All' : 'Select All'}
            </Button>
          )}
        </div>

        <div className="flex-1 min-h-0 relative overflow-hidden">
          <div className="absolute inset-0 overflow-y-auto overflow-x-hidden">
            <Spin spinning={loading}>
              {filteredPermissions.length === 0 ? (
                <Empty
                  description={
                    searchText
                      ? 'No permissions found matching your search'
                      : 'No permissions available'
                  }
                />
              ) : (
                <Space direction="vertical" className="w-full" size="middle">
                  {filteredPermissions.map((permission) => {
                    const isChecked = assignedPermissionIds.includes(permission.id);
                    return (
                      <div
                        key={permission.id}
                        className={`p-3 border rounded-lg transition-colors ${
                          isChecked
                            ? 'border-primary bg-primary-muted'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <Checkbox
                          checked={isChecked}
                          onChange={() => handleTogglePermission(permission.id)}
                          className="w-full"
                        >
                          <div className="ml-2">
                            <div className="font-medium text-gray-900">
                              {permission.key}
                            </div>
                            {permission.description && (
                              <div className="text-sm text-gray-600 mt-1">
                                {permission.description}
                              </div>
                            )}
                          </div>
                        </Checkbox>
                      </div>
                    );
                  })}
                </Space>
              )}
            </Spin>
          </div>
        </div>
      </div>
    </Drawer>
  );
}
